/**
 * Admin — governance audit trail. Every permission event lands here:
 * position assignments, grant edits, delegations, revocations (with cascade
 * counts), queued actions, approvals/rejections and reason-backed overrides.
 * Read-only by design — an audit trail nobody can edit is the point.
 * Wired: GET /api/positions/audit.
 */
import React from 'react';
import { View } from 'react-native';

import { space } from '@bero/tokens';
import { str, useGateList } from '@/components/gate/api';
import { ModerationScreen } from '@/features/account-admin/bits';
import { useTheme } from '@/theme';
import { AppText, Card, Pill } from '@/ui';

type Row = {
  id: string; kind: string; detail: string; reason: string;
  actorName: string; createdAt: string;
};

const KIND_LABEL: Record<string, { label: string; status: 'pending' | 'approved' | 'rejected' }> = {
  assign: { label: 'ASSIGNED', status: 'approved' },
  unassign: { label: 'REMOVED', status: 'rejected' },
  edit_grants: { label: 'GRANTS', status: 'pending' },
  delegate: { label: 'SHARED', status: 'approved' },
  revoke: { label: 'REVOKED', status: 'rejected' },
  queue: { label: 'QUEUED', status: 'pending' },
  approve: { label: 'APPROVED', status: 'approved' },
  reject: { label: 'REJECTED', status: 'rejected' },
  override: { label: 'OVERRIDE', status: 'rejected' },
};

function toRow(v: unknown, i: number): Row {
  return {
    id: str(v, 'id') ?? `a${i}`,
    kind: str(v, 'kind') ?? '',
    detail: str(v, 'detail') ?? '',
    reason: str(v, 'reason') ?? '',
    actorName: str(v, 'actorName') ?? 'System',
    createdAt: str(v, 'createdAt') ?? '',
  };
}

function fmtWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
    + ', ' + d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' });
}

export default function AdminAuditScreen() {
  const { colors } = useTheme();
  const { items, state } = useGateList<unknown>('/api/positions/audit?per=100', 'audit');
  const rows = items.map(toRow);

  return (
    <ModerationScreen
      title="Audit trail"
      state={state}
      count={rows.length}
      emptyIcon="file-text"
      emptyTitle="Nothing recorded yet"
      emptyBody="Permission changes, approvals and overrides are recorded here permanently."
    >
      {rows.map((r) => {
        const k = KIND_LABEL[r.kind] ?? { label: r.kind.toUpperCase(), status: 'pending' as const };
        return (
          <Card key={r.id}>
            <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: space.s2 }}>
              <View style={{ flex: 1, minWidth: 0 }}>
                <AppText variant="bodyMd" weight="500">{r.detail || '—'}</AppText>
                <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>
                  {r.actorName} · {fmtWhen(r.createdAt)}
                </AppText>
                {r.reason ? (
                  <AppText variant="bodySm" style={{ marginTop: 4, color: colors.accent700 }}>
                    Reason: &ldquo;{r.reason}&rdquo;
                  </AppText>
                ) : null}
              </View>
              <Pill status={k.status} label={k.label} />
            </View>
          </Card>
        );
      })}
    </ModerationScreen>
  );
}
